<template>
  <v-dialog :value="value" fullscreen persistent :dark="isDarkMode" :light="!isDarkMode" @input="onDialogInput">
    <v-card class="mes-shell" :class="{ 'mes-light': !isDarkMode }">
      <div class="mes-header">
        <div class="d-flex align-center" style="gap: 12px">
          <v-btn icon @click="requestClose"><v-icon>mdi-close</v-icon></v-btn>
          <div class="mes-title-icon"><v-icon color="white">mdi-account-cog</v-icon></div>
          <div>
            <div class="text-subtitle-1 font-weight-bold">Mitarbeiter-Einstellungen</div>
            <div class="text-caption mes-muted">{{ formattedMonth }} · Änderungen gelten nur für diesen Monat</div>
          </div>
        </div>
        <v-chip small outlined color="green accent-3">
          <v-icon left x-small>mdi-shield-check</v-icon>
          Bestehende Schichten bleiben unverändert
        </v-chip>
      </div>

      <div class="mes-layout">
        <aside class="mes-sidebar">
          <v-text-field
            v-model="search"
            dense
            outlined
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            label="Mitarbeiter suchen"
            class="ma-3"></v-text-field>
          <div class="mes-employee-list">
            <button
              v-for="employee in filteredEmployees"
              :key="employee._id"
              type="button"
              class="mes-employee"
              :class="{ active: selectedEmployee && String(selectedEmployee._id) === String(employee._id) }"
              @click="selectEmployee(employee)">
              <v-avatar size="38" class="mr-3">
                <img :src="employee.picture ? '/img/profilbilder' + employee.picture : '/img/default-avatar.png'" />
              </v-avatar>
              <div class="text-left min-width-0">
                <div class="text-body-2 font-weight-bold text-truncate">{{ employee.fullName }}</div>
                <div class="text-caption mes-muted">
                  {{ summaryFor(employee).contractPercentage || 0 }}% ·
                  {{ formatHours(summaryFor(employee).total && summaryFor(employee).total.netTargetHours) }} Netto gesamt
                </div>
                <div class="mes-sidebar-states mt-1">
                  <span class="mes-sidebar-state" :class="isPlanningReady(employee) ? 'ready' : 'inactive'">
                    <v-icon x-small>{{ isPlanningReady(employee) ? "mdi-check-circle" : "mdi-pause-circle" }}</v-icon>
                    {{ isPlanningReady(employee) ? "Planung aktiv" : "Planung aus" }}
                  </span>
                  <span v-if="hasShiftInPlan(employee)" class="mes-sidebar-state planned">
                    <v-icon x-small>mdi-calendar-check</v-icon> bereits im Plan
                  </span>
                </div>
              </div>
              <v-icon v-if="hasMonthlyOverride(employee)" x-small color="green accent-3" class="ml-auto">mdi-calendar-check</v-icon>
            </button>
            <div v-if="filteredEmployees.length === 0" class="pa-5 text-center text-caption mes-muted">Keine Mitarbeiter gefunden.</div>
          </div>
        </aside>

        <main v-if="selectedEmployee && draft" class="mes-main">
          <div class="mes-employee-header">
            <div class="d-flex align-center">
              <v-avatar size="54" class="mr-3 elevation-2">
                <img :src="selectedEmployee.picture ? '/img/profilbilder' + selectedEmployee.picture : '/img/default-avatar.png'" />
              </v-avatar>
              <div>
                <div class="text-h6 font-weight-bold">{{ selectedEmployee.fullName }}</div>
                <div class="text-caption mes-muted">Monatskonfiguration für {{ formattedMonth }}</div>
              </div>
            </div>
            <div class="d-flex align-center" style="gap: 8px">
              <v-chip small label :color="draft.isReadyForPlanning ? 'green' : 'orange'" text-color="white">
                {{ draft.isReadyForPlanning ? "Bereit für Planung" : "Nicht für Planung freigegeben" }}
              </v-chip>
              <v-chip v-if="isDirty" small outlined color="amber"><v-icon left x-small>mdi-circle-edit-outline</v-icon>Ungespeichert</v-chip>
            </div>
          </div>

          <div class="mes-content">
            <v-row>
              <v-col cols="12" lg="6">
                <section class="mes-section">
                  <div class="mes-section-title"><v-icon small color="green accent-3">mdi-chart-donut</v-icon>Pensum & Soll</div>
                  <v-card outlined class="mes-summary-card mb-4">
                    <div class="mes-summary-heading">
                      <div>
                        <span class="mes-summary-eyebrow">Monatliche Soll-Berechnung</span>
                        <strong>{{ currentSummary.contractPercentage || 0 }}% Pensum</strong>
                      </div>
                      <v-chip x-small outlined color="green accent-3">alle Studios</v-chip>
                    </div>

                    <div class="mes-summary-row">
                      <span>
                        Monats-Soll vor Ferien/Abwesenheiten
                        <v-tooltip bottom max-width="340">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon v-bind="attrs" v-on="on" x-small class="ml-1 mes-info-icon">mdi-information-outline</v-icon>
                          </template>
                          <span>
                            Berechnet aus {{ currentSummary.targetWorkdays || 0 }} Soll-Arbeitstagen à
                            {{ formatPreciseHours(currentSummary.dailyTargetHours) }}. Wochenenden, Feiertage, Ein-/Austritt und aktive Schultage sind
                            im Sollkalender bereits berücksichtigt.
                          </span>
                        </v-tooltip>
                      </span>
                      <strong>{{ formatHours(currentHoursBreakdown.grossTargetHours) }}</strong>
                    </div>
                    <div class="mes-summary-row mes-deduction-row">
                      <span><v-icon x-small>mdi-beach</v-icon>Ferien</span>
                      <strong>{{ negativeHours(currentHoursBreakdown.vacationHours) }}</strong>
                    </div>
                    <div class="mes-summary-row mes-deduction-row">
                      <span><v-icon x-small>mdi-calendar-remove</v-icon>Abwesenheiten</span>
                      <strong>{{ negativeHours(currentHoursBreakdown.absenceHours) }}</strong>
                    </div>
                    <div v-if="currentHoursBreakdown.adjustmentHours" class="mes-summary-row mes-adjustment-row">
                      <span><v-icon x-small>mdi-calculator-variant</v-icon>Manuelle Soll-Korrektur</span>
                      <strong>{{ signedHours(currentHoursBreakdown.adjustmentHours) }}</strong>
                    </div>
                    <div class="mes-summary-row mes-net-row">
                      <span>Netto-Monatssoll</span>
                      <strong>{{ formatHours(currentHoursBreakdown.globalTargetHours) }}</strong>
                    </div>
                    <div class="mes-summary-row mes-global-plan-row">
                      <span>In allen Studios bereits geplant</span>
                      <strong>{{ formatHours(currentHoursBreakdown.totalPlannedHours) }}</strong>
                    </div>

                    <div class="mes-studio-target">
                      <div class="mes-summary-row">
                        <span><v-icon x-small color="cyan lighten-2">mdi-domain-switch</v-icon>Tatsächlich in anderen Studios geplant</span>
                        <strong class="cyan--text text--lighten-2">{{ negativeHours(currentHoursBreakdown.externalPlannedHours) }}</strong>
                      </div>
                      <div class="mes-summary-row mes-assistant-target-row">
                        <span><v-icon small color="green accent-3">mdi-robot-outline</v-icon>Assistenten-Ziel in diesem Studio</span>
                        <strong>{{ formatHours(currentHoursBreakdown.studioTargetHours) }}</strong>
                      </div>
                      <div class="mes-summary-row mes-local-plan-row">
                        <span>Hier bereits geplant</span>
                        <strong>{{ formatHours(currentHoursBreakdown.localPlannedHours) }}</strong>
                      </div>
                    </div>
                  </v-card>

                  <v-switch
                    v-model="draft.isReadyForPlanning"
                    color="green accent-3"
                    inset
                    label="Für die Planung freigeben"
                    hint="Beeinflusst künftige automatische Vorschläge, nicht bestehende Zuweisungen."
                    persistent-hint></v-switch>

                  <v-text-field
                    :value="draft.percentages[studioId]"
                    @input="setPercentage"
                    type="number"
                    min="0"
                    max="200"
                    outlined
                    dense
                    clearable
                    suffix="%"
                    label="Pensum für diesen Monat"
                    :placeholder="`Statistik ${basePercentage}%`"
                    :hint="`Leer = Stellenprozent (Statistik) ${basePercentage}%. Ein Wert gilt nur für ${monthId}; 0% erzeugt kein Soll.`"
                    persistent-hint
                    class="mt-5"></v-text-field>

                  <v-switch
                    v-model="draft.offsetOvertime"
                    color="orange"
                    inset
                    label="Überstunden mit dem Monatssoll verrechnen"
                    hint="Übernimmt beim Aktivieren den aktuellen Überstundensaldo als Soll-Korrektur."
                    persistent-hint
                    @change="toggleOvertimeOffset"></v-switch>

                  <v-expand-transition>
                    <v-text-field
                      v-if="draft.offsetOvertime"
                      v-model.number="draft.customSollAdjustment"
                      type="number"
                      outlined
                      dense
                      suffix="h"
                      label="Manuelle Soll-Korrektur"
                      hint="Negativ reduziert, positiv erhöht das Monatssoll."
                      persistent-hint></v-text-field>
                  </v-expand-transition>

                  <v-switch
                    v-model="draft.groupShifts"
                    color="green"
                    inset
                    label="Schichten möglichst in Blöcken gruppieren"
                    hint="Besonders für kleine Pensen: Einsätze nach Möglichkeit an aufeinanderfolgenden Tagen planen."
                    persistent-hint></v-switch>
                </section>

                <section class="mes-section mt-4">
                  <div class="mes-section-title"><v-icon small color="blue lighten-2">mdi-school</v-icon>Schultage</div>
                  <v-switch v-model="draft.isApprentice" dense inset color="blue" label="In diesem Monat als Lernende:r berücksichtigen"></v-switch>
                  <v-expand-transition>
                    <div v-if="draft.isApprentice" class="pl-1">
                      <div class="text-caption mes-muted mb-2">Schultage reduzieren das Soll ausserhalb der hinterlegten Schulferien.</div>
                      <v-chip-group :value="schoolDayNumbers" multiple column @change="setSchoolDays">
                        <v-chip v-for="day in weekdays.slice(0, 5)" :key="'school-' + day.value" small filter :value="day.value" color="blue">
                          {{ day.short }}
                        </v-chip>
                      </v-chip-group>
                      <v-switch v-model="draft.longCommute" dense color="blue" label="Langer Schulweg"></v-switch>
                      <v-alert v-if="schoolShiftConflicts.length" dense text type="warning" class="mt-3 mb-0">
                        <strong
                          >{{ schoolShiftConflicts.length }} bestehende {{ schoolShiftConflicts.length === 1 ? "Schicht" : "Schichten" }}</strong
                        >
                        {{ schoolShiftConflicts.length === 1 ? "liegt" : "liegen" }} nach diesen Einstellungen an einem Schultag:
                        {{ schoolShiftConflictDates }}. Die Zuweisungen bleiben bestehen und werden danach im Einsatzplan als Konflikt markiert.
                      </v-alert>
                    </div>
                  </v-expand-transition>
                </section>
              </v-col>

              <v-col cols="12" lg="6">
                <section class="mes-section">
                  <div class="mes-section-title"><v-icon small color="red lighten-2">mdi-calendar-remove</v-icon>Wiederkehrende Sperrtage</div>
                  <div class="text-caption mes-muted mb-2">An diesen Wochentagen wird die Person nicht automatisch vorgeschlagen.</div>
                  <v-chip-group v-model="draft.recurringAbsence" multiple column>
                    <v-chip v-for="day in weekdays" :key="'blocked-' + day.value" small filter :value="day.value" color="red">{{ day.short }}</v-chip>
                  </v-chip-group>
                </section>

                <section class="mes-section mt-4">
                  <div class="mes-section-title"><v-icon small color="cyan lighten-2">mdi-domain-switch</v-icon>Tage in anderem Studio</div>
                  <div class="text-caption mes-muted mb-2">
                    Diese Wochentage sperren die Person hier für automatische Vorschläge. Die Auswahl selbst verändert keine Sollstunden.
                  </div>
                  <v-chip-group v-model="draft.recurringExternalStudio" multiple column>
                    <v-chip v-for="day in weekdays" :key="'external-' + day.value" small filter :value="day.value" color="cyan">{{
                      day.short
                    }}</v-chip>
                  </v-chip-group>
                  <div class="mes-external-impact mt-3">
                    <v-icon small color="cyan lighten-2">mdi-information-outline</v-icon>
                    <div>
                      <strong>Wirkung im Assistenten</strong>
                      <span>
                        Nur tatsächlich in anderen Studios geplante Schichten werden berücksichtigt:
                        {{ formatHours(currentHoursBreakdown.globalTargetHours) }} Netto-Soll gesamt −
                        {{ formatHours(currentHoursBreakdown.externalPlannedHours) }} extern geplant =
                        <strong>{{ formatHours(currentHoursBreakdown.studioTargetHours) }} Ziel in diesem Studio.</strong>
                      </span>
                    </div>
                  </div>
                </section>

                <section class="mes-section mt-4">
                  <div class="mes-section-title"><v-icon small color="pink lighten-2">mdi-account-heart</v-icon>Team-Präferenzen</div>
                  <v-autocomplete
                    v-model="draft.preferredPartners"
                    :items="partnerOptions"
                    item-text="fullName"
                    item-value="userId"
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    outlined
                    dense
                    hide-details
                    label="Bevorzugte Teampartner"
                    placeholder="Mitarbeiter auswählen"></v-autocomplete>
                </section>

                <section class="mes-section mt-4">
                  <div class="mes-section-title"><v-icon small color="purple lighten-2">mdi-pin</v-icon>Fixe Schichten</div>
                  <div class="text-caption mes-muted mb-3">Die Vorgabe gilt für künftige automatische Zuweisungen in diesem Monat.</div>
                  <div v-for="day in weekdays" :key="'fixed-' + day.value" class="mes-fixed-row">
                    <span class="text-caption font-weight-bold">{{ day.short }}</span>
                    <v-select
                      v-model="draft.fixedShifts[day.value]"
                      :items="availableShifts"
                      item-text="name"
                      item-value="_id"
                      dense
                      outlined
                      hide-details
                      clearable
                      placeholder="Automatisch">
                      <template v-slot:selection="{ item }">
                        <span class="mes-color-dot" :style="{ background: item.color || '#777' }"></span>{{ item.name }}
                      </template>
                    </v-select>
                  </div>
                </section>
              </v-col>
            </v-row>

            <section class="mes-section mt-4">
              <div class="d-flex align-start justify-space-between flex-wrap mb-3" style="gap: 10px">
                <div>
                  <div class="mes-section-title mb-1">
                    <v-icon small color="amber lighten-2">mdi-calendar-account</v-icon>Abwesenheiten & Monatsereignisse
                  </div>
                  <div class="text-caption mes-muted">Nur zur Information – diese Einträge werden hier nicht verändert.</div>
                </div>
                <div class="d-flex flex-wrap justify-end" style="gap: 6px">
                  <v-chip v-for="count in monthEntryCounts" :key="count.type" x-small outlined :color="count.color">
                    <v-icon left x-small>{{ count.icon }}</v-icon
                    >{{ count.label }}: {{ count.value }}
                  </v-chip>
                </div>
              </div>

              <div v-if="selectedMonthEntries.length" class="mes-event-list">
                <div v-for="entry in selectedMonthEntries" :key="entry.key" class="mes-event-row">
                  <v-chip small label :color="entry.color" text-color="white" class="mes-event-type">
                    <v-icon left x-small>{{ entry.icon }}</v-icon
                    >{{ entry.label }}
                  </v-chip>
                  <div class="mes-event-date">{{ entry.dateLabel }}</div>
                  <div class="mes-event-info">
                    <div class="text-body-2 font-weight-medium">{{ entry.info }}</div>
                    <div v-if="entry.detail" class="text-caption mes-muted mt-1">{{ entry.detail }}</div>
                  </div>
                  <v-chip v-if="entry.statusText" x-small outlined :color="entry.statusColor || entry.color">{{ entry.statusText }}</v-chip>
                </div>
              </div>
              <div v-else class="mes-empty-events">
                <v-icon color="green accent-3" class="mr-2">mdi-calendar-check-outline</v-icon>
                Keine Ferien, Wünsche, Abwesenheiten oder Spezialzuweisungen in diesem Monat.
              </div>
            </section>
          </div>

          <div class="mes-footer">
            <div class="d-flex align-center text-caption mes-muted">
              <v-icon small color="green accent-3" class="mr-2">mdi-lock-check</v-icon>
              Speichern ändert keine bestehende Schichtzuweisung.
            </div>
            <div>
              <v-btn text class="mr-2" @click="resetDraft" :disabled="!isDirty || previewing || saving">Zurücksetzen</v-btn>
              <v-btn
                depressed
                color="green accent-3"
                class="black--text font-weight-bold"
                @click="preparePreview"
                :disabled="!isDirty"
                :loading="previewing">
                <v-icon left small>mdi-eye-check</v-icon>Auswirkungen prüfen
              </v-btn>
            </div>
          </div>
        </main>
      </div>

      <v-dialog v-model="previewDialog" max-width="680" :dark="isDarkMode" :light="!isDarkMode" persistent>
        <v-card class="rounded-xl">
          <v-card-title class="d-flex align-center">
            <v-icon color="green accent-3" class="mr-3">mdi-clipboard-text-search</v-icon>
            <div>
              <div class="text-subtitle-1 font-weight-bold">Auswirkungen vor dem Speichern</div>
              <div class="text-caption mes-muted">{{ selectedEmployee && selectedEmployee.fullName }} · {{ formattedMonth }}</div>
            </div>
          </v-card-title>
          <v-card-text v-if="previewSummary">
            <div class="mes-impact-grid mb-4">
              <div>
                <span>Pensum</span><strong>{{ currentSummary.contractPercentage || 0 }}% → {{ previewSummary.contractPercentage || 0 }}%</strong>
              </div>
              <div>
                <span>Netto-Soll · alle Studios</span>
                <strong :class="targetDeltaClass"
                  >{{ formatHours(currentHoursBreakdown.globalTargetHours) }} → {{ formatHours(previewHoursBreakdown.globalTargetHours) }}</strong
                >
              </div>
              <div>
                <span>Fremdstudio bereits geplant</span><strong>{{ formatHours(previewHoursBreakdown.externalPlannedHours) }}</strong>
              </div>
              <div>
                <span>Assistenten-Ziel · dieses Studio</span>
                <strong :class="assistantTargetDeltaClass">
                  {{ formatHours(currentHoursBreakdown.studioTargetHours) }} → {{ formatHours(previewHoursBreakdown.studioTargetHours) }}
                </strong>
              </div>
              <div>
                <span>Hier bereits geplant</span><strong>{{ formatHours(previewHoursBreakdown.localPlannedHours) }}</strong>
              </div>
              <div>
                <span>Neue Differenz hier</span><strong>{{ signedHours(previewHoursBreakdown.studioDifferenceHours) }}</strong>
              </div>
            </div>

            <div class="text-caption font-weight-bold text-uppercase mes-muted mb-2">Geänderte Planungsregeln</div>
            <v-list dense class="transparent pa-0 mb-3">
              <v-list-item v-for="change in ruleChanges" :key="change.label" class="px-0 mes-change-row">
                <v-list-item-icon class="mr-3"
                  ><v-icon small :color="change.color">{{ change.icon }}</v-icon></v-list-item-icon
                >
                <v-list-item-content
                  ><v-list-item-title class="text-body-2">{{ change.label }}</v-list-item-title></v-list-item-content
                >
                <v-list-item-action-text>{{ change.value }}</v-list-item-action-text>
              </v-list-item>
              <div v-if="ruleChanges.length === 0" class="text-caption mes-muted py-2">Keine weiteren Planungsregeln geändert.</div>
            </v-list>

            <v-alert v-if="schoolShiftConflicts.length" dense text type="warning" class="mb-3">
              {{ schoolShiftConflicts.length }} bestehende
              {{ schoolShiftConflicts.length === 1 ? "Schicht kollidiert" : "Schichten kollidieren" }} mit den neuen Schultagen ({{
                schoolShiftConflictDates
              }}). Im Einsatzplan kannst du jede davon gezielt prüfen, freigeben, verschieben oder bewusst so belassen.
            </v-alert>
            <v-alert dense text type="info" class="mb-0">
              Bestehende Schichten bleiben genau wie sie sind. Die neuen Regeln wirken auf künftige Vorschläge im Assistenten.
            </v-alert>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="previewDialog = false" :disabled="saving">Weiter bearbeiten</v-btn>
            <v-btn color="green accent-3" depressed class="black--text font-weight-bold" @click="saveMonthlySettings" :loading="saving">
              Nur Einstellungen speichern
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="discardDialog" max-width="440" :dark="isDarkMode" :light="!isDarkMode">
        <v-card class="rounded-xl">
          <v-card-title class="text-subtitle-1 font-weight-bold">Ungespeicherte Änderungen verwerfen?</v-card-title>
          <v-card-text>Die Änderungen für {{ selectedEmployee && selectedEmployee.fullName }} wurden noch nicht gespeichert.</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="discardDialog = false">Weiter bearbeiten</v-btn>
            <v-btn text color="error" @click="discardAndContinue">Verwerfen</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import api from "@/api"

export default {
  name: "ShiftManagementMonthEmployeeSettingsDialog",
  props: {
    value: { type: Boolean, default: false },
    studioId: { type: String, required: true },
    monthId: { type: String, required: true },
    startDate: { type: String, required: true },
    endDate: { type: String, required: true },
    employees: { type: Array, default: () => [] },
    summaries: { type: Object, default: () => ({}) },
    availableShifts: { type: Array, default: () => [] },
    planShifts: { type: Array, default: () => [] },
    schoolHolidays: { type: Array, default: () => [] },
    publicHolidays: { type: Array, default: () => [] },
    initialEmployeeId: { type: String, default: null },
    isDarkMode: { type: Boolean, default: true },
  },
  data() {
    return {
      search: "",
      selectedEmployee: null,
      draft: null,
      initialDraft: null,
      previewDialog: false,
      previewSummary: null,
      previewing: false,
      saving: false,
      discardDialog: false,
      pendingAction: null,
      weekdays: [
        { value: 1, short: "Mo", name: "Montag" },
        { value: 2, short: "Di", name: "Dienstag" },
        { value: 3, short: "Mi", name: "Mittwoch" },
        { value: 4, short: "Do", name: "Donnerstag" },
        { value: 5, short: "Fr", name: "Freitag" },
        { value: 6, short: "Sa", name: "Samstag" },
        { value: 7, short: "So", name: "Sonntag" },
      ],
    }
  },
  computed: {
    formattedMonth() {
      return this.$moment(this.monthId, "YYYY-MM").format("MMMM YYYY")
    },
    eligibleEmployees() {
      const seen = new Set()
      return this.employees
        .filter((employee) => {
          if (!employee || seen.has(String(employee._id))) return false
          const belongsToStudio = (employee.studio || []).some((studio) => String(studio._id) === String(this.studioId))
          if (!belongsToStudio) return false
          seen.add(String(employee._id))
          return true
        })
        .sort((left, right) => String(left.fullName || "").localeCompare(String(right.fullName || ""), "de"))
    },
    filteredEmployees() {
      const needle = String(this.search || "")
        .trim()
        .toLowerCase()
      if (!needle) return this.eligibleEmployees
      return this.eligibleEmployees.filter((employee) =>
        String(employee.fullName || "")
          .toLowerCase()
          .includes(needle),
      )
    },
    isDirty() {
      return Boolean(
        this.draft &&
          this.initialDraft &&
          JSON.stringify(this.normalizedDraft(this.draft)) !== JSON.stringify(this.normalizedDraft(this.initialDraft)),
      )
    },
    selectedUserId() {
      if (!this.selectedEmployee) return ""
      return String(this.selectedEmployee.user?._id || this.selectedEmployee.user || "")
    },
    currentSummary() {
      return this.summaries[this.selectedUserId] || { contractPercentage: 0, total: {} }
    },
    currentHoursBreakdown() {
      return this.summaryHoursBreakdown(this.currentSummary)
    },
    previewHoursBreakdown() {
      return this.summaryHoursBreakdown(this.previewSummary)
    },
    schoolDayNumbers() {
      return (this.draft?.schoolDays || []).map((day) => Number(day.dayOfWeek ?? day))
    },
    schoolShiftConflicts() {
      if (!this.selectedUserId || !this.draft?.isApprentice) return []
      return (this.planShifts || [])
        .filter((shift) => {
          if (!shift || shift.isVirtualExternal || !shift.assignedEmployee || !this.isCurrentStudioShift(shift)) return false
          const assignedId = String(shift.assignedEmployee._id || shift.assignedEmployee)
          return assignedId === this.selectedUserId && this.isDraftSchoolDay(shift.date)
        })
        .sort((left, right) => String(left.date).localeCompare(String(right.date)))
    },
    schoolShiftConflictDates() {
      return [...new Set(this.schoolShiftConflicts.map((shift) => this.$moment(shift.date).format("DD.MM")))].join(", ")
    },
    selectedMonthEntries() {
      return this.buildMonthEntries()
    },
    monthEntryCounts() {
      const definitions = {
        special: { label: "Spezial", color: "purple", icon: "mdi-star" },
        vacation: { label: "Ferien", color: "blue", icon: "mdi-beach" },
        freeWish: { label: "Frei", color: "red", icon: "mdi-close-circle" },
        shiftWish: { label: "Wünsche", color: "green", icon: "mdi-clock-outline" },
        absence: { label: "Abwesenheit", color: "orange", icon: "mdi-calendar-remove" },
        publicHoliday: { label: "Feiertage", color: "amber darken-2", icon: "mdi-star-four-points" },
        schoolHoliday: { label: "Schulferien", color: "teal", icon: "mdi-school-outline" },
      }
      const counts = this.selectedMonthEntries.reduce((result, entry) => {
        result[entry.type] = (result[entry.type] || 0) + 1
        return result
      }, {})
      return Object.keys(definitions)
        .filter((type) => counts[type] > 0)
        .map((type) => ({ type, value: counts[type], ...definitions[type] }))
    },
    basePercentage() {
      if (!this.selectedEmployee) return 0
      const studio = (this.selectedEmployee.studio || []).find((entry) => String(entry._id) === String(this.studioId))
      if (studio?.percentage === undefined || studio?.percentage === null || studio?.percentage === "") return 0
      const percentage = Number(String(studio.percentage).replace(",", "."))
      return Number.isFinite(percentage) ? Math.max(0, percentage) : 0
    },
    partnerOptions() {
      return this.eligibleEmployees
        .filter((employee) => String(employee.user?._id || employee.user || "") !== this.selectedUserId)
        .map((employee) => ({
          userId: String(employee.user?._id || employee.user || ""),
          fullName: employee.fullName,
        }))
        .filter((employee) => employee.userId)
    },
    ruleChanges() {
      if (!this.draft || !this.initialDraft) return []
      const changes = []
      const beforeBlocked = this.dayLabels(this.initialDraft.recurringAbsence)
      const afterBlocked = this.dayLabels(this.draft.recurringAbsence)
      const beforeExternal = this.dayLabels(this.initialDraft.recurringExternalStudio)
      const afterExternal = this.dayLabels(this.draft.recurringExternalStudio)
      const beforeFixed = this.fixedShiftCount(this.initialDraft.fixedShifts)
      const afterFixed = this.fixedShiftCount(this.draft.fixedShifts)

      if (beforeBlocked !== afterBlocked)
        changes.push({ label: "Sperrtage", value: afterBlocked || "Keine", icon: "mdi-calendar-remove", color: "red" })
      if (beforeExternal !== afterExternal)
        changes.push({ label: "Anderes Studio", value: afterExternal || "Keine", icon: "mdi-domain-switch", color: "cyan" })
      if (beforeFixed !== afterFixed)
        changes.push({ label: "Fixe Schichten", value: `${beforeFixed} → ${afterFixed}`, icon: "mdi-pin", color: "purple" })
      if (this.initialDraft.groupShifts !== this.draft.groupShifts) {
        changes.push({ label: "Blockplanung", value: this.draft.groupShifts ? "Aktiv" : "Inaktiv", icon: "mdi-link-variant", color: "green" })
      }
      if (
        this.initialDraft.offsetOvertime !== this.draft.offsetOvertime ||
        Number(this.initialDraft.customSollAdjustment) !== Number(this.draft.customSollAdjustment)
      ) {
        changes.push({
          label: "Soll-Korrektur",
          value: this.draft.offsetOvertime ? this.signedHours(this.draft.customSollAdjustment) : "Inaktiv",
          icon: "mdi-timer-sync-outline",
          color: "orange",
        })
      }
      if (
        JSON.stringify([...(this.initialDraft.preferredPartners || [])].map(String).sort()) !==
        JSON.stringify([...(this.draft.preferredPartners || [])].map(String).sort())
      ) {
        changes.push({
          label: "Teampartner",
          value: this.partnerNames(this.draft.preferredPartners) || "Keine",
          icon: "mdi-account-heart",
          color: "pink",
        })
      }
      if (this.dayLabels(this.schoolDayNumbers) !== this.dayLabels((this.initialDraft.schoolDays || []).map((day) => day.dayOfWeek ?? day))) {
        changes.push({ label: "Schultage", value: this.dayLabels(this.schoolDayNumbers) || "Keine", icon: "mdi-school", color: "blue" })
      }
      if (Number(this.initialDraft.customSollAdjustment || 0) !== Number(this.draft.customSollAdjustment || 0)) {
        changes.push({
          label: "Soll-Korrektur",
          value: `${this.signedHours(this.initialDraft.customSollAdjustment)} → ${this.signedHours(this.draft.customSollAdjustment)}`,
          icon: "mdi-calculator-variant",
          color: "amber",
        })
      }
      if (this.initialDraft.isApprentice !== this.draft.isApprentice) {
        changes.push({ label: "Schulmodell", value: this.draft.isApprentice ? "Aktiv" : "Inaktiv", icon: "mdi-school-outline", color: "blue" })
      }
      if (this.initialDraft.longCommute !== this.draft.longCommute) {
        changes.push({ label: "Langer Schulweg", value: this.draft.longCommute ? "Ja" : "Nein", icon: "mdi-train-car", color: "blue-grey" })
      }
      if (this.initialDraft.isReadyForPlanning !== this.draft.isReadyForPlanning) {
        changes.push({
          label: "Planungsstatus",
          value: this.draft.isReadyForPlanning ? "Bereit" : "Nicht bereit",
          icon: "mdi-check-circle",
          color: "green",
        })
      }
      return changes
    },
    targetDeltaClass() {
      const before = this.currentHoursBreakdown.globalTargetHours
      const after = this.previewHoursBreakdown.globalTargetHours
      if (after > before) return "orange--text"
      if (after < before) return "green--text"
      return ""
    },
    assistantTargetDeltaClass() {
      const before = this.currentHoursBreakdown.studioTargetHours
      const after = this.previewHoursBreakdown.studioTargetHours
      if (after > before) return "orange--text"
      if (after < before) return "green--text"
      return ""
    },
  },
  watch: {
    value(open) {
      if (open) this.initializeSelection(this.initialEmployeeId)
    },
    initialEmployeeId(employeeId) {
      if (this.value && employeeId && !this.isDirty) this.initializeSelection(employeeId)
    },
    employees() {
      if (this.value && !this.isDirty) this.initializeSelection(this.selectedEmployee && this.selectedEmployee._id)
    },
  },
  methods: {
    clone(value) {
      return JSON.parse(JSON.stringify(value || {}))
    },
    initializeSelection(preferredId) {
      const employee = this.eligibleEmployees.find((entry) => String(entry._id) === String(preferredId)) || this.eligibleEmployees[0] || null
      this.loadEmployee(employee)
    },
    loadEmployee(employee) {
      this.selectedEmployee = employee
      if (!employee) {
        this.draft = null
        this.initialDraft = null
        return
      }

      const studioSettings = this.clone(employee.shiftPlanningSettings?.[this.studioId] || {})
      const monthly = this.clone(studioSettings.monthlyOverrides?.[this.monthId] || {})
      delete studioSettings.monthlyOverrides
      const effective = this.mergeSettings(studioSettings, monthly)
      const fixedShifts = { ...(effective.fixedShifts || {}) }
      for (let day = 1; day <= 7; day++) if (!(day in fixedShifts)) fixedShifts[day] = null

      this.draft = {
        percentages: { ...(effective.percentages || {}), [this.studioId]: effective.percentages?.[this.studioId] ?? null },
        customSollAdjustment: Number(effective.customSollAdjustment || 0),
        recurringAbsence: [...(effective.recurringAbsence || [])].map(Number),
        recurringExternalStudio: [...(effective.recurringExternalStudio || [])].map(Number),
        fixedShifts,
        isReadyForPlanning: effective.isReadyForPlanning === true,
        longCommute: effective.longCommute === true,
        groupShifts: effective.groupShifts === true,
        offsetOvertime: effective.offsetOvertime === true,
        preferredPartners: [...(effective.preferredPartners || [])].map(String),
        isApprentice: typeof effective.isApprentice === "boolean" ? effective.isApprentice : employee.isApprentice === true,
        schoolDays: this.clone(Array.isArray(effective.schoolDays) ? effective.schoolDays : employee.schoolDays || []),
      }
      this.initialDraft = this.clone(this.draft)
      this.previewSummary = null
    },
    mergeSettings(base, override) {
      return {
        ...(base || {}),
        ...(override || {}),
        // Nur die direkt für diesen Monat gespeicherten Prozente sind ein
        // Override. Alte allgemeine Prozentwerte werden bewusst ignoriert.
        percentages: { ...(override?.percentages || {}) },
        fixedShifts: { ...(base?.fixedShifts || {}), ...(override?.fixedShifts || {}) },
      }
    },
    normalizedDraft(value) {
      const normalized = this.clone(value)
      normalized.recurringAbsence = [...(normalized.recurringAbsence || [])].map(Number).sort()
      normalized.recurringExternalStudio = [...(normalized.recurringExternalStudio || [])].map(Number).sort()
      normalized.schoolDays = [...(normalized.schoolDays || [])].map((day) => Number(day.dayOfWeek ?? day)).sort()
      normalized.preferredPartners = [...(normalized.preferredPartners || [])].map(String).sort()
      return normalized
    },
    summaryFor(employee) {
      const userId = String(employee?.user?._id || employee?.user || "")
      return this.summaries[userId] || { contractPercentage: 0, total: {} }
    },
    summaryHoursBreakdown(summary) {
      const total = summary?.total || {}
      const numberOrZero = (value) => {
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : 0
      }
      const globalTargetHours = numberOrZero(total.netTargetHours)
      const totalPlannedHours = numberOrZero(total.plannedHours)
      const localPlannedHours = numberOrZero(summary?.byStudio?.[String(this.studioId)]?.plannedHours)
      const externalPlannedHours = Math.max(0, totalPlannedHours - localPlannedHours)
      const studioTargetHours = Math.max(0, globalTargetHours - externalPlannedHours)

      return {
        grossTargetHours: numberOrZero(total.grossTargetHours),
        vacationHours: numberOrZero(total.vacationHours),
        absenceHours: numberOrZero(total.absenceHours),
        adjustmentHours: numberOrZero(total.adjustmentHours),
        globalTargetHours,
        totalPlannedHours,
        localPlannedHours,
        externalPlannedHours,
        studioTargetHours,
        studioDifferenceHours: localPlannedHours - studioTargetHours,
      }
    },
    effectiveSettingsFor(employee) {
      const studioSettings = this.clone(employee?.shiftPlanningSettings?.[this.studioId] || {})
      const monthly = this.clone(studioSettings.monthlyOverrides?.[this.monthId] || {})
      delete studioSettings.monthlyOverrides
      return this.mergeSettings(studioSettings, monthly)
    },
    isPlanningReady(employee) {
      if (this.selectedEmployee && this.draft && String(this.selectedEmployee._id) === String(employee?._id)) {
        return this.draft.isReadyForPlanning === true
      }
      return this.effectiveSettingsFor(employee).isReadyForPlanning === true
    },
    hasShiftInPlan(employee) {
      const userId = String(employee?.user?._id || employee?.user || "")
      if (!userId) return false
      return (this.planShifts || []).some(
        (shift) => shift?.assignedEmployee && String(shift.assignedEmployee._id || shift.assignedEmployee) === userId,
      )
    },
    isCurrentStudioShift(shift) {
      if (!shift?.studio) return true
      return String(shift.studio._id || shift.studio) === String(this.studioId)
    },
    isInsideSchoolHoliday(date) {
      const target = this.$moment(date)
      return (this.schoolHolidays || []).some(
        (holiday) => holiday.startDate && holiday.endDate && target.isBetween(holiday.startDate, holiday.endDate, "day", "[]"),
      )
    },
    isDraftSchoolDay(date) {
      if (!this.draft?.isApprentice) return false
      const weekday = this.$moment(date).isoWeekday()
      const configured = (this.draft.schoolDays || []).some((schoolDay) => Number(schoolDay?.dayOfWeek ?? schoolDay) === weekday)
      return configured && !this.isInsideSchoolHoliday(date)
    },
    entryOverlapsMonth(startDate, endDate = startDate) {
      if (!startDate || !endDate) return false
      return this.$moment(startDate).isSameOrBefore(this.endDate, "day") && this.$moment(endDate).isSameOrAfter(this.startDate, "day")
    },
    sortedEntryDates(dates) {
      return [...(dates || [])].filter((date) => date && date.date).sort((left, right) => String(left.date).localeCompare(String(right.date)))
    },
    formatEntryRange(dates) {
      const sorted = this.sortedEntryDates(dates)
      if (!sorted.length) return ""
      if (sorted.length === 1) return this.$moment(sorted[0].date).format("dd, DD.MM")
      return `${this.$moment(sorted[0].date).format("DD.MM")} – ${this.$moment(sorted[sorted.length - 1].date).format("DD.MM")}`
    },
    formatShiftWishType(type) {
      return { morning: "Frühschicht", intermediate: "Mittelschicht", late: "Spätschicht" }[type] || type || "Schichtwunsch"
    },
    formatShiftTimes(shift) {
      const times = shift?.times?.length ? shift.times : shift?.shiftTemplate?.times || []
      if (!times.length) return "Keine Zeit hinterlegt"
      return `${times[0].start} – ${times[times.length - 1].end}`
    },
    buildMonthEntries() {
      if (!this.selectedEmployee) return []
      const employee = this.selectedEmployee
      const userId = this.selectedUserId
      const entries = []
      const add = (entry) => entries.push(entry)

      ;(this.planShifts || [])
        .filter((shift) => {
          if (!shift || !shift.assignedEmployee || !this.isCurrentStudioShift(shift) || !this.entryOverlapsMonth(shift.date)) return false
          const assignedId = String(shift.assignedEmployee._id || shift.assignedEmployee)
          const isSpecial = shift.isSpecialDemand === true || String(shift.allocationNote || "").includes("Spezialtag")
          return assignedId === userId && isSpecial
        })
        .forEach((shift, index) =>
          add({
            key: `special-${shift._id || index}`,
            type: "special",
            label: "Spezial",
            color: "purple",
            icon: "mdi-star",
            sortDate: shift.date,
            dateLabel: this.$moment(shift.date).format("dd, DD.MM"),
            info: shift.shiftTemplate?.name || shift.allocationNote || "Spezial-Einsatz",
            detail: this.formatShiftTimes(shift),
            statusText: "Fix zugeteilt",
            statusColor: "purple",
          }),
        )
      ;(employee.holidayEntries || [])
        .filter((vacation) => {
          if (vacation.status && vacation.status !== "accepted") return false
          const dates = this.sortedEntryDates(vacation.dates)
          return dates.length && this.entryOverlapsMonth(dates[0].date, dates[dates.length - 1].date)
        })
        .forEach((vacation, index) => {
          const dates = this.sortedEntryDates(vacation.dates)
          add({
            key: `vacation-${vacation._id || index}`,
            type: "vacation",
            label: "Ferien",
            color: "blue",
            icon: "mdi-beach",
            sortDate: dates[0].date,
            dateLabel: this.formatEntryRange(dates),
            info: vacation.reason || vacation.note || "Genehmigte Ferien",
            statusText: "Bestätigt",
            statusColor: "green",
          })
        })
      ;(employee.freeWishEntries || [])
        .filter((wish) => (!wish.status || wish.status === "accepted") && this.entryOverlapsMonth(wish.day))
        .forEach((wish, index) =>
          add({
            key: `free-${wish._id || index}`,
            type: "freeWish",
            label: "Frei",
            color: "red",
            icon: "mdi-close-circle",
            sortDate: wish.day,
            dateLabel: this.$moment(wish.day).format("dd, DD.MM"),
            info: wish.comment || wish.note || "Freiwunsch für den ganzen Tag",
            statusText: "Bestätigt",
            statusColor: "green",
          }),
        )
      ;(employee.shiftWishEntries || [])
        .filter((wish) => (!wish.status || wish.status === "accepted") && this.entryOverlapsMonth(wish.day))
        .forEach((wish, index) =>
          add({
            key: `shift-wish-${wish._id || index}`,
            type: "shiftWish",
            label: "Wunsch",
            color: "green",
            icon: "mdi-clock-outline",
            sortDate: wish.day,
            dateLabel: this.$moment(wish.day).format("dd, DD.MM"),
            info: this.formatShiftWishType(wish.type),
            detail: wish.comment || wish.note || "",
            statusText: "Bestätigt",
            statusColor: "green",
          }),
        )

      const absenceLabels = {
        sick: { label: "Krank", color: "red", icon: "mdi-emoticon-sad" },
        school: { label: "Schule", color: "blue", icon: "mdi-school" },
        military: { label: "Militär", color: "deep-purple", icon: "mdi-shield" },
        pregnancy: { label: "Mutterschaft", color: "teal", icon: "mdi-baby-carriage" },
        other: { label: "Abwesenheit", color: "grey", icon: "mdi-alert-circle-outline" },
      }
      ;(employee.sickEntries || [])
        .filter((absence) => {
          if (absence.status === "rejected") return false
          const dates = this.sortedEntryDates(absence.sickDays)
          return dates.length && this.entryOverlapsMonth(dates[0].date, dates[dates.length - 1].date)
        })
        .forEach((absence, index) => {
          const dates = this.sortedEntryDates(absence.sickDays)
          const appearance = absenceLabels[absence.absenceReason] || absenceLabels.other
          const partialDay = dates.find((day) => Number(day.sickPercentage ?? day.percentage ?? 100) < 100)
          const percentage = partialDay ? Number(partialDay.sickPercentage ?? partialDay.percentage ?? 100) : 100
          add({
            key: `absence-${absence._id || index}`,
            type: "absence",
            label: appearance.label,
            color: appearance.color,
            icon: appearance.icon,
            sortDate: dates[0].date,
            dateLabel: this.formatEntryRange(dates),
            info: percentage < 100 ? `${percentage}% ${appearance.label.toLowerCase()}` : appearance.label,
            detail: absence.note || "",
            statusText: absence.status === "accepted" ? "Bestätigt" : "Ausstehend",
            statusColor: absence.status === "accepted" ? "green" : "orange",
          })
        })
      ;(this.publicHolidays || [])
        .filter((holiday) => this.entryOverlapsMonth(holiday.date || holiday.startDate))
        .forEach((holiday, index) => {
          const date = holiday.date || holiday.startDate
          add({
            key: `public-holiday-${holiday._id || date || index}`,
            type: "publicHoliday",
            label: "Feiertag",
            color: "amber darken-2",
            icon: "mdi-star-four-points",
            sortDate: date,
            dateLabel: this.$moment(date).format("dd, DD.MM"),
            info: holiday.name || "Feiertag",
          })
        })

      if (this.draft?.isApprentice) {
        ;(this.schoolHolidays || [])
          .filter((holiday) => this.entryOverlapsMonth(holiday.startDate, holiday.endDate))
          .forEach((holiday, index) =>
            add({
              key: `school-holiday-${holiday._id || index}`,
              type: "schoolHoliday",
              label: "Schulferien",
              color: "teal",
              icon: "mdi-school-outline",
              sortDate: holiday.startDate,
              dateLabel: `${this.$moment(holiday.startDate).format("DD.MM")} – ${this.$moment(holiday.endDate).format("DD.MM")}`,
              info: holiday.name || "Hinterlegte Schulferien",
              detail: "Konfigurierte Schultage gelten in diesem Zeitraum als normale Arbeitstage.",
            }),
          )
      }

      return entries.sort(
        (left, right) => String(left.sortDate || "").localeCompare(String(right.sortDate || "")) || left.label.localeCompare(right.label, "de"),
      )
    },
    hasMonthlyOverride(employee) {
      return Boolean(employee?.shiftPlanningSettings?.[this.studioId]?.monthlyOverrides?.[this.monthId])
    },
    selectEmployee(employee) {
      if (this.selectedEmployee && String(employee._id) === String(this.selectedEmployee._id)) return
      if (this.isDirty) {
        this.pendingAction = () => this.loadEmployee(employee)
        this.discardDialog = true
        return
      }
      this.loadEmployee(employee)
    },
    setPercentage(value) {
      this.$set(this.draft.percentages, this.studioId, value === "" || value === undefined ? null : value)
    },
    setSchoolDays(days) {
      this.draft.schoolDays = (days || []).map((dayOfWeek) => ({ dayOfWeek: Number(dayOfWeek), hours: 8.4 }))
    },
    toggleOvertimeOffset(enabled) {
      this.draft.customSollAdjustment = enabled ? -Math.round(Number(this.selectedEmployee?.overTime || 0)) : 0
    },
    resetDraft() {
      this.draft = this.clone(this.initialDraft)
      this.previewSummary = null
    },
    requestClose() {
      if (this.isDirty) {
        this.pendingAction = () => this.$emit("input", false)
        this.discardDialog = true
        return
      }
      this.$emit("input", false)
    },
    discardAndContinue() {
      this.discardDialog = false
      this.draft = this.clone(this.initialDraft)
      const action = this.pendingAction
      this.pendingAction = null
      if (action) action()
    },
    onDialogInput(open) {
      if (!open) this.requestClose()
    },
    async preparePreview() {
      if (!this.selectedUserId || !this.isDirty) return
      this.previewing = true
      try {
        const response = await api.shiftManagement.fetchWorkHoursSummaries([this.studioId], this.startDate, this.endDate, {
          [this.selectedUserId]: this.draft,
        })
        const summary = response?.summaries?.[this.selectedUserId]
        if (!summary || !summary.total) throw new Error("Vorschau fehlt")
        this.previewSummary = summary
        this.previewDialog = true
      } catch (error) {
        console.error("Fehler bei der Einstellungs-Vorschau", error)
        this.$toast.error("Die Auswirkungen konnten nicht berechnet werden.")
      } finally {
        this.previewing = false
      }
    },
    async saveMonthlySettings() {
      if (!this.selectedEmployee || !this.previewSummary) return
      this.saving = true
      try {
        await api.shiftManagement.saveMonthlyPlanningSettings(this.selectedEmployee._id, this.studioId, this.monthId, this.draft)
        const payload = {
          employeeId: String(this.selectedEmployee._id),
          userId: this.selectedUserId,
          employeeName: this.selectedEmployee.fullName,
          settings: this.clone(this.draft),
          summary: this.previewSummary,
        }
        this.initialDraft = this.clone(this.draft)
        this.previewDialog = false
        this.$emit("saved", payload)
        this.$emit("input", false)
      } catch (error) {
        console.error("Fehler beim Speichern der Monatseinstellungen", error)
        this.$toast.error("Monatseinstellungen konnten nicht gespeichert werden.")
      } finally {
        this.saving = false
      }
    },
    dayLabels(values) {
      const numbers = [...new Set((values || []).map(Number))].sort()
      return numbers
        .map((value) => this.weekdays.find((day) => day.value === value)?.short)
        .filter(Boolean)
        .join(", ")
    },
    fixedShiftCount(value) {
      return Object.values(value || {}).filter(Boolean).length
    },
    partnerNames(values) {
      const names = new Map(this.partnerOptions.map((employee) => [String(employee.userId), employee.fullName]))
      return (values || [])
        .map((value) => names.get(String(value)))
        .filter(Boolean)
        .join(", ")
    },
    formatHours(value) {
      const number = Number(value || 0)
      return `${number.toFixed(1)}h`
    },
    formatPreciseHours(value) {
      const number = Number(value || 0)
      return `${number.toFixed(2)}h`
    },
    negativeHours(value) {
      const number = Math.abs(Number(value || 0))
      return number > 0 ? `−${number.toFixed(1)}h` : "0.0h"
    },
    signedHours(value) {
      const number = Number(value || 0)
      return `${number > 0 ? "+" : ""}${number.toFixed(1)}h`
    },
  },
}
</script>

<style scoped>
.mes-shell {
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #17191d;
  color: white;
}
.mes-header {
  min-height: 68px;
  padding: 10px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.28);
}
.mes-title-icon {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #43a047, #1b5e20);
}
.mes-layout {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 285px 1fr;
}
.mes-sidebar {
  min-height: 0;
  display: flex;
  flex-direction: column;
  border-right: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.18);
}
.mes-employee-list {
  overflow-y: auto;
  padding: 0 10px 16px;
}
.mes-employee {
  width: 100%;
  min-height: 58px;
  border: 0;
  border-radius: 11px;
  padding: 8px 10px;
  margin-bottom: 4px;
  color: inherit;
  background: transparent;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.mes-employee > .min-width-0 {
  flex: 1;
}
.mes-employee:hover {
  background: rgba(255, 255, 255, 0.05);
}
.mes-employee.active {
  background: rgba(76, 175, 80, 0.14);
  box-shadow: inset 3px 0 #69f0ae;
}
.mes-sidebar-states {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
.mes-sidebar-state {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 5px;
  border-radius: 999px;
  font-size: 0.62rem;
  line-height: 1.2;
  font-weight: 700;
  white-space: nowrap;
}
.mes-sidebar-state .v-icon {
  color: inherit !important;
}
.mes-sidebar-state.ready {
  color: #69f0ae;
  background: rgba(105, 240, 174, 0.1);
}
.mes-sidebar-state.inactive {
  color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.06);
}
.mes-sidebar-state.planned {
  color: #82b1ff;
  background: rgba(130, 177, 255, 0.1);
}
.mes-main {
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.mes-employee-header {
  padding: 16px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.mes-content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
}
.mes-section {
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 14px;
  padding: 16px;
  background: rgba(255, 255, 255, 0.025);
}
.mes-section-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 700;
  margin-bottom: 14px;
}
.mes-summary-card {
  padding: 14px;
  background: rgba(0, 0, 0, 0.15) !important;
}
.mes-summary-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 11px;
  margin-bottom: 7px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}
.mes-summary-heading > div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.mes-summary-heading strong {
  font-size: 0.9rem;
}
.mes-summary-eyebrow {
  font-size: 0.64rem;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}
.mes-summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 29px;
  padding: 4px 0;
  font-size: 0.78rem;
}
.mes-summary-row > span {
  display: inline-flex;
  min-width: 0;
  align-items: center;
  gap: 6px;
}
.mes-summary-row > strong {
  flex: 0 0 auto;
}
.mes-summary-row .v-icon {
  color: inherit;
}
.mes-deduction-row {
  padding-left: 10px;
}
.mes-deduction-row strong {
  color: #ffab91;
}
.mes-adjustment-row {
  padding-left: 10px;
}
.mes-adjustment-row strong {
  color: #ffd180;
}
.mes-net-row {
  min-height: 36px;
  margin-top: 4px;
  padding-top: 9px;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
  font-size: 0.85rem;
}
.mes-net-row span,
.mes-net-row strong {
  color: #fff;
}
.mes-studio-target {
  margin-top: 10px;
  padding: 10px 11px;
  border: 1px solid rgba(0, 188, 212, 0.2);
  border-radius: 11px;
  background: linear-gradient(135deg, rgba(0, 188, 212, 0.09), rgba(76, 175, 80, 0.04));
}
.mes-assistant-target-row {
  min-height: 38px;
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid rgba(0, 188, 212, 0.14);
  font-size: 0.84rem;
}
.mes-assistant-target-row span,
.mes-assistant-target-row strong {
  color: #69f0ae;
}
.mes-local-plan-row {
  min-height: 24px;
  padding-top: 1px;
  font-size: 0.72rem;
}
.mes-global-plan-row {
  min-height: 24px;
  padding-top: 1px;
  font-size: 0.72rem;
}
.mes-info-icon {
  cursor: help;
  opacity: 0.72;
}
.mes-summary-card span,
.mes-muted {
  color: rgba(255, 255, 255, 0.52);
}
.mes-summary-card .mes-net-row span,
.mes-summary-card .mes-net-row strong {
  color: #fff;
}
.mes-summary-card .mes-assistant-target-row span,
.mes-summary-card .mes-assistant-target-row strong {
  color: #69f0ae;
}
.mes-external-impact {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  padding: 10px 11px;
  border: 1px solid rgba(0, 188, 212, 0.18);
  border-radius: 10px;
  background: rgba(0, 188, 212, 0.055);
}
.mes-external-impact > div {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}
.mes-external-impact strong {
  color: #80deea;
  font-size: 0.74rem;
}
.mes-external-impact span {
  color: rgba(255, 255, 255, 0.58);
  font-size: 0.7rem;
  line-height: 1.45;
}
.mes-external-impact span strong {
  color: #69f0ae;
  font-size: inherit;
}
.mes-fixed-row {
  display: grid;
  grid-template-columns: 34px 1fr;
  gap: 8px;
  align-items: center;
  margin-bottom: 7px;
}
.mes-color-dot {
  display: inline-block;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  margin-right: 7px;
}
.mes-footer {
  min-height: 66px;
  padding: 10px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.24);
}
.mes-impact-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.mes-impact-grid > div {
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 10px;
  padding: 11px;
  display: flex;
  flex-direction: column;
}
.mes-impact-grid span {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.5);
}
.mes-impact-grid strong {
  margin-top: 3px;
}
.mes-change-row {
  min-height: 38px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}
.mes-event-list {
  display: flex;
  flex-direction: column;
  gap: 7px;
}
.mes-event-row {
  min-height: 54px;
  display: grid;
  grid-template-columns: 125px 105px minmax(160px, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 9px 10px;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 11px;
  background: rgba(0, 0, 0, 0.13);
}
.mes-event-type {
  justify-self: start;
  min-width: 94px;
  justify-content: flex-start;
}
.mes-event-date {
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}
.mes-event-info {
  min-width: 0;
}
.mes-empty-events {
  min-height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.52);
  font-size: 0.82rem;
  border: 1px dashed rgba(255, 255, 255, 0.1);
  border-radius: 11px;
}
.min-width-0 {
  min-width: 0;
}
.mes-light {
  background: #f5f6f8;
  color: #1d1d1f;
}
.mes-light .mes-header,
.mes-light .mes-sidebar,
.mes-light .mes-footer {
  background: rgba(255, 255, 255, 0.88);
  border-color: rgba(0, 0, 0, 0.08);
}
.mes-light .mes-muted,
.mes-light .mes-summary-card span,
.mes-light .mes-impact-grid span {
  color: rgba(0, 0, 0, 0.52);
}
.mes-light .mes-employee:hover {
  background: rgba(0, 0, 0, 0.04);
}
.mes-light .mes-section,
.mes-light .mes-summary-card {
  border-color: rgba(0, 0, 0, 0.1);
  background: white !important;
}
.mes-light .mes-summary-heading,
.mes-light .mes-net-row {
  border-color: rgba(0, 0, 0, 0.09);
}
.mes-light .mes-net-row span,
.mes-light .mes-net-row strong {
  color: #17202a;
}
.mes-light .mes-studio-target {
  border-color: rgba(0, 137, 123, 0.19);
  background: linear-gradient(135deg, rgba(0, 188, 212, 0.075), rgba(76, 175, 80, 0.045));
}
.mes-light .mes-assistant-target-row {
  border-color: rgba(0, 137, 123, 0.14);
}
.mes-light .mes-assistant-target-row span,
.mes-light .mes-assistant-target-row strong {
  color: #087f5b;
}
.mes-light .mes-external-impact {
  border-color: rgba(0, 137, 123, 0.17);
  background: rgba(0, 137, 123, 0.045);
}
.mes-light .mes-external-impact > div > strong {
  color: #087f8c;
}
.mes-light .mes-external-impact span {
  color: rgba(0, 0, 0, 0.58);
}
.mes-light .mes-external-impact span strong {
  color: #087f5b;
}
.mes-light .mes-sidebar-state.inactive {
  color: rgba(0, 0, 0, 0.52);
  background: rgba(0, 0, 0, 0.05);
}
.mes-light .mes-event-row {
  border-color: rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.018);
}
.mes-light .mes-empty-events {
  color: rgba(0, 0, 0, 0.5);
  border-color: rgba(0, 0, 0, 0.1);
}
@media (max-width: 900px) {
  .mes-layout {
    grid-template-columns: 220px 1fr;
  }
  .mes-header > .v-chip {
    display: none;
  }
  .mes-employee-header {
    align-items: flex-start;
    gap: 10px;
  }
  .mes-footer {
    align-items: flex-start;
    gap: 10px;
  }
  .mes-event-row {
    grid-template-columns: 110px 90px minmax(120px, 1fr);
  }
  .mes-event-row > .v-chip:last-child {
    grid-column: 3;
    justify-self: start;
  }
}
</style>
